import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/Services.css';

const serviceData = [
    {
        id: 'flight-booking',
        title: 'Flight Booking',
        description: 'Find the best deals on flights worldwide with our comprehensive booking system.',
        longDescription: 'Our flight booking service offers access to a global network of airlines, providing you with the best options for domestic and international travel. We secure competitive prices, flexible booking options, and 24/7 support for any changes or emergencies.',
        features: [
            'Domestic and international flight options',
            'Competitive pricing and fare comparison',
            'Special deals and seasonal discounts',
            'Group booking facilities',
            'Emergency booking assistance'
        ],
        icon: '✈️',
        color: '#4A90E2',
        image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'car-rental',
        title: 'Car Rental',
        description: 'Rent vehicles worldwide with our hassle-free car rental service.',
        longDescription: 'Our car rental service provides a wide range of vehicles to suit your needs, from economy cars to luxury vehicles. We partner with trusted rental companies worldwide to ensure quality service, competitive rates, and flexible pickup and drop-off options.',
        features: [
            'Wide range of vehicles - economy to luxury',
            'Flexible pickup and drop-off locations',
            'Comprehensive insurance options',
            'GPS and child seat add-ons available',
            '24/7 roadside assistance'
        ],
        icon: '🚗',
        color: '#50C878',
        image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'hajj-umrah',
        title: 'Hajj/Umrah',
        description: 'Complete spiritual journey packages with expert guidance and support.',
        longDescription: 'Our Hajj and Umrah services are designed to provide a spiritually fulfilling experience with comprehensive support. We handle visa processing, accommodation near the holy sites, transportation, and provide experienced guides to assist you throughout your sacred journey.',
        features: [
            'Visa processing assistance',
            'Accommodation near holy sites',
            'Transportation between rituals',
            'Experienced religious guides',
            'Pre-journey orientation sessions'
        ],
        icon: '🕌',
        color: '#9B59B6',
        image: 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'cruise',
        title: 'Cruise',
        description: 'Luxury cruise packages to destinations around the globe.',
        longDescription: 'Experience the ultimate in travel luxury with our cruise packages. From Caribbean adventures to Mediterranean explorations, we offer a wide selection of cruise lines and itineraries. Enjoy all-inclusive amenities, entertainment, and the opportunity to visit multiple destinations in a single journey.',
        features: [
            'Wide selection of cruise lines and destinations',
            'All-inclusive packages available',
            'Family-friendly and adults-only options',
            'Shore excursion planning',
            'Special occasion celebration packages'
        ],
        icon: '🚢',
        color: '#3498DB',
        image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'tour-packages',
        title: 'Tour Packages',
        description: 'Customized tour packages for solo travelers, families, and groups.',
        longDescription: 'Our tour packages are crafted to provide unforgettable experiences for every type of traveler. Whether you\'re seeking adventure, relaxation, cultural immersion, or family fun, we design itineraries that match your interests, preferences, and budget. Each package includes carefully selected accommodations, guided tours, and unique local experiences.',
        features: [
            'Customized itineraries for all interests',
            'Expert local guides in each destination',
            'Carefully selected accommodations',
            'Authentic cultural experiences',
            'Flexible scheduling and private tour options'
        ],
        icon: '🌎',
        color: '#E67E22',
        image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'travel-insurance',
        title: 'Travel Insurance',
        description: 'Comprehensive travel insurance for worry-free journeys.',
        longDescription: 'Our travel insurance options provide peace of mind for your journeys. We offer coverage for medical emergencies, trip cancellations, lost baggage, and other unexpected events. Our policies are flexible and can be tailored to your specific travel needs, whether you\'re taking a short domestic trip or an extended international adventure.',
        features: [
            'Medical emergency coverage worldwide',
            'Trip cancellation and interruption protection',
            'Lost baggage and personal belongings coverage',
            'Flight delay and missed connection benefits',
            'Emergency evacuation services'
        ],
        icon: '🛡️',
        color: '#E74C3C',
        image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    },
    {
        id: 'passport-visa',
        title: 'Passport/Visa',
        description: 'Expert assistance with passport and visa processing.',
        longDescription: 'Navigating the complex world of international travel documentation is our specialty. Our passport and visa services include application assistance, document verification, expedited processing, and personalized guidance through each step of the process. We stay updated on the latest requirements for countries worldwide to ensure your documentation is always in order.',
        features: [
            'Visa application assistance for all countries',
            'Passport renewal and application support',
            'Document verification and preparation',
            'Expedited processing options',
            'Regular updates on changing requirements'
        ],
        icon: '🛂',
        color: '#2ECC71',
        image: '/Assets/image/passport.jpg'
    },
    {
        id: 'forex',
        title: 'Forex',
        description: 'Competitive foreign exchange rates for all major currencies.',
        longDescription: 'Our foreign exchange services offer competitive rates for all major world currencies. We provide convenient currency exchange options, travel cards, and expert advice on managing your money while traveling internationally. Our goal is to help you maximize your budget and minimize exchange-related stress during your journey.',
        features: [
            'Competitive exchange rates for major currencies',
            'Multi-currency travel cards',
            'Foreign currency cash delivery',
            'Buy-back guarantees',
            'Currency exchange advice and planning'
        ],
        icon: '💱',
        color: '#F1C40F',
        image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80'
    }
];

const Service = () => {
    const [selectedService, setSelectedService] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12,
                duration: 0.5
            }
        }
    };

    const openModal = (service) => {
        setSelectedService(service);
        setIsModalOpen(true);
        // Prevent body scrolling when modal is open
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setIsModalOpen(false);
        // Re-enable body scrolling
        document.body.style.overflow = 'auto';
    };

    return (
        <>
            <div className="services-page">
                {/* Hero Section with Background Image */}
                <div className="services-hero">
                    <div className="services-hero-image">
                        <img src="https://images.unsplash.com/photo-1488085061387-422e29b40080?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80" alt="Travel Services" />
                    </div>

                    <div className="services-hero-overlay"></div>

                    <div className="services-hero-content">
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            Comprehensive Travel Services
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            From flight bookings to custom tour packages, we provide end-to-end travel solutions tailored to your needs
                        </motion.p>
                        <motion.div
                            className="services-hero-cta"
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <a href="#services-list" className="primary-button">
                                Explore Our Services
                            </a>
                            <Link to="/contact" className="secondary-button">
                                Contact Us
                            </Link>
                        </motion.div>
                    </div>
                </div>

                {/* Background Image for the entire page */}
                <div className="services-background-image">
                    <img src="/Assets/image/Services-page.jpg" alt="Services-Background" />
                </div>

            <div className="sr-services-intro">
                <motion.div
                    className="sr-intro-content"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="sr-title gradient-text">Why Choose Our Services?</h2>
                    <p className="sr-description">
                        At Excellent Travels, we pride ourselves on delivering exceptional travel services that go beyond the ordinary.
                        With years of experience in the travel industry, we understand the nuances of creating memorable journeys.
                    </p>
                    <div className="sr-benefits-grid">
                        <div className="sr-benefit-item">
                            <div className="sr-benefit-icon">🌟</div>
                            <h3 className="sr-benefit-title">Personalized Approach</h3>
                            <p className="sr-benefit-text">Tailored travel solutions designed specifically for your preferences and needs</p>
                        </div>
                        <div className="sr-benefit-item">
                            <div className="sr-benefit-icon">💼</div>
                            <h3 className="sr-benefit-title">Expert Guidance</h3>
                            <p className="sr-benefit-text">Professional travel consultants with extensive knowledge and experience</p>
                        </div>
                        <div className="sr-benefit-item">
                            <div className="sr-benefit-icon">🔒</div>
                            <h3 className="sr-benefit-title">Reliable Support</h3>
                            <p className="sr-benefit-text">24/7 assistance throughout your journey for complete peace of mind</p>
                        </div>
                        <div className="sr-benefit-item">
                            <div className="sr-benefit-icon">💰</div>
                            <h3 className="sr-benefit-title">Value for Money</h3>
                            <p className="sr-benefit-text">Competitive pricing without compromising on quality or experience</p>
                        </div>
                    </div>
                </motion.div>
            </div>

            <div id="services-list" className="services-main">
                <motion.div
                    className="services-header"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="gradient-text">Our Comprehensive Services</h2>
                    <p>Discover our wide range of travel services designed to make your journey seamless and memorable</p>
                </motion.div>

                <motion.div
                    className="services-grid"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {serviceData.map((service, index) => (
                        <motion.div
                            key={service.id}
                            variants={itemVariants}
                            className="service-card"
                            style={{
                                borderColor: service.color
                            }}
                            onClick={() => openModal(service)}
                        >
                            <div className="service-card-content">
                                <div className="service-content-inner">
                                    <div className="service-icon" style={{ backgroundColor: `${service.color}20`, color: service.color }}>
                                        <span>{service.icon}</span>
                                    </div>
                                    <h3>{service.title}</h3>
                                    <p>{service.description}</p>
                                    <div style={{ width: '100%', textAlign: 'center' }}>
                                        <button
                                            className="service-details-button"
                                            style={{ backgroundColor: service.color }}
                                        >
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="service-card-bg" style={{ backgroundImage: `url(${service.image})` }}></div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            <div className="services-testimonials">
                <motion.div
                    className="testimonials-content"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="gradient-text">What Our Clients Say</h2>
                    <div className="testimonials-grid">
                        <div className="testimonial-card">
                            <div className="testimonial-quote">"The flight booking service was exceptional. I got a great deal and the customer support was available 24/7 during my journey."</div>
                            <div className="testimonial-author">
                                <div className="testimonial-avatar">RA</div>
                                <div className="testimonial-info">
                                    <h4>Rahul Agarwal</h4>
                                    <p>Business Traveler</p>
                                </div>
                            </div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-quote">"Our family tour package was perfectly organized. Every detail was taken care of, and we could just focus on enjoying our vacation."</div>
                            <div className="testimonial-author">
                                <div className="testimonial-avatar">SP</div>
                                <div className="testimonial-info">
                                    <h4>Sunita Patel</h4>
                                    <p>Family Traveler</p>
                                </div>
                            </div>
                        </div>
                        <div className="testimonial-card">
                            <div className="testimonial-quote">"The Hajj package was comprehensive and spiritually fulfilling. The guides were knowledgeable and supportive throughout the journey."</div>
                            <div className="testimonial-author">
                                <div className="testimonial-avatar">AK</div>
                                <div className="testimonial-info">
                                    <h4>Ahmed Khan</h4>
                                    <p>Religious Pilgrim</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            <div className="services-cta">
                <motion.div
                    className="cta-content"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2>Ready to Start Your Journey?</h2>
                    <p>Contact our travel experts today to plan your perfect trip</p>
                    <Link to="/contact" className="cta-button">
                        Get in Touch
                        <span className="arrow-icon">→</span>
                    </Link>
                </motion.div>
            </div>

            {/* Service Detail Modal */}
            <AnimatePresence>
                {isModalOpen && selectedService && (
                    <motion.div
                        className="service-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                    >
                        <motion.div
                            className="service-modal"
                            initial={{ opacity: 0, y: 100, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 100, scale: 0.9 }}
                            transition={{ type: "spring", damping: 15 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button className="modal-close" onClick={closeModal}>×</button>
                            <div className="modal-image" style={{ backgroundImage: `url(${selectedService.image})` }}>
                                <div className="modal-image-overlay" style={{ backgroundColor: 'transparent' }}></div>
                                <div className="modal-title">
                                    <div className="modal-icon" style={{ backgroundColor: selectedService.color }}>
                                        <span>{selectedService.icon}</span>
                                    </div>
                                    <h2>{selectedService.title}</h2>
                                </div>
                            </div>
                            <div className="modal-content">
                                <p className="modal-description">{selectedService.longDescription}</p>
                                <div className="modal-features">
                                    <h3>Key Features</h3>
                                    <ul>
                                        {selectedService.features.map((feature, index) => (
                                            <motion.li
                                                key={index}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index }}
                                            >
                                                {feature}
                                            </motion.li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="modal-actions">
                                    <Link
                                        to={`/services/${selectedService.id}`}
                                        className="modal-button"
                                        style={{ backgroundColor: selectedService.color }}
                                    >
                                        Learn More
                                    </Link>
                                    <Link
                                        to="/contact"
                                        className="modal-button outline"
                                        style={{ color: selectedService.color, borderColor: selectedService.color }}
                                    >
                                        Contact Us
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            </div> {/* Closing tag for services-page div */}
        </>
    );
};

export default Service;